import { listFieldValue, type FieldValueListEntry } from '../../../../core/components/InputManager/types.ts'
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Form, Row, Col, Spin } from 'antd'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { useSelector, useDispatch } from 'react-redux'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { useDrawer } from '../../../../core/contexts/DrawerContext.jsx'
import { updateActiveRecord } from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { selectLookupOptions, useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../core/components/AccountSourcingFields.tsx'
import { ProspectFields, type ProspectFormHelpers } from '../../utils/fieldConfigs.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../../state_manager/constants/lookupCategories.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { getProspectInfoFields, getNewInvestorFields, getProspectTeamFields } from '../../utils/fieldConfigs.ts'
import { transformFormDataToProspectRecord } from '../../utils/prospectFormTransformers.ts'
import { getInitialFormDataForNew, transformSystemUserToContact } from '../../utils/prospectFormInitializers.ts'
import { fetchCompanySourcingContacts } from '../../../companies/services/companiesService.ts'
import { UserEntryFields } from '../../../../state_manager/data_models/user_entry.ts'
import {
  UNKNOWN_CONTACT,
  SECTION_PROSPECT_OVERVIEW,
  SECTION_PROSPECT_TEAM
} from '../../../../state_manager/constants/strings.ts'
import { handleFieldChange as applyBusinessRules } from '../../utils/prospectFormBusinessRules.ts'
import { validateRequiredFields } from '../../utils/prospectFormValidation.ts'
import { summariseFieldErrors } from '../../../../core/utils/utilities.ts'
import { dismissErrorToast, showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'
import { validateProspectRecord, ProspectRecordFields } from '../../../../state_manager/data_models/prospect_record.ts'
import { PIPELINE_TYPE_FUNDRAISING, FIELD_KEY_LABEL } from '../../../../state_manager/globals.ts'
import type { FundsModel } from '../../../../state_manager/data_models/funds_model.ts'
import {
  getDefaultPipelineConfigForPipeline,
  isPipelineStageValidForPipeline
} from '../../utils/pipelineFundStageRules.ts'
import { useFundFieldOptions } from '../../../global_configs/hooks/useFundFieldOptions.tsx'
import { useStageMoveRights } from '../../../../core/hooks/useStageMoveRights.ts'
import type { RootState } from '../../../../state_manager/store.ts'
import type { ProspectContactsByRole } from '../../utils/prospectContacts.ts'

type ValidationSection = 'prospectInfo' | 'newInvestorInfo' | 'prospectTeam'
// The three role lists as the drawer holds them: the CONTACT fields' onChange
// hands over option objects or bare ids (listFieldValue), so this is wider than
// the derived ProspectContactsByRole an existing prospect's tabs consume.
type NewProspectContacts = Record<keyof ProspectContactsByRole, FieldValueListEntry[]>
const VALIDATION_SECTIONS: ValidationSection[] = ['prospectInfo', 'newInvestorInfo', 'prospectTeam']
type SectionErrors = ReturnType<typeof validateRequiredFields>['errors']

// Keyed error toasts: dismissed by the drawer itself once the condition
// clears (validation passes / the drawer closes) — see the deals drawer.
const REQUIRED_FIELDS_TOAST = 'prospect-drawer:required-fields'
const SUBMIT_FAILED_TOAST = 'prospect-drawer:submit-failed'

function pipelineOriginationForFundId(fundId: string | null | undefined, funds: FundsModel[]): string | null {
  if (!fundId) return null
  const row = funds.find((f) => f.id === fundId)
  const po = row?.pipeline_origination
  return typeof po === 'string' && po.length > 0 ? po : null
}

// The investor field holds a bare id or the select's {value, label} pair.
function extractCompanyId(companyValue: any): string | null {
  if (!companyValue) return null
  if (typeof companyValue === 'object') {
    return companyValue.value || companyValue.id || null
  }
  return typeof companyValue === 'string' && companyValue.trim() === '' ? null : companyValue
}

export const Drawer = forwardRef<
  { submit: () => Promise<void> },
  { prospect?: any | null; onSubmit?: (data: any) => Promise<void>; loading?: boolean }
>(({ prospect = null, onSubmit, loading = false }, ref) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()
  const { isNew } = useDrawer()
  const [form] = Form.useForm()
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()

  const tempRecordUuid = useMemo(() => {
    if (isNew) {
      return 'fundraising-new'
    }
    return null
  }, [isNew])

  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])
  const currentUser = useSelector((state: any) => state.globalConfigs?.current_user || null)
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])
  const pipelineStages = useSelector((state: RootState) => state.globalConfigs?.pipeline_stages || [])
  const pipelineConfigs = useSelector((state: RootState) => state.globalConfigs?.pipeline_configs || [])
  const fundOptions = useSelector((state: { globalConfigs?: { funds?: FundsModel[] } }) => state.globalConfigs?.funds || [])
  const currencyOptions = getCurrencyOptions()
  const industryOptions = useSelector((state: any) => selectLookupOptions(state, LOOKUP_CATEGORY_INDUSTRY))

  const defaultFundraisingPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline: { default?: boolean; type?: string; id?: string }) =>
        pipeline.default === true && pipeline.type === PIPELINE_TYPE_FUNDRAISING
    ) as { id?: string } | undefined

    return defaultPipeline?.id || null
  }, [pipelines])

  const [formData, setFormData] = useState<ReturnType<typeof getInitialFormDataForNew>>(() => {
    if (prospect && Object.keys(prospect).length > 0) {
      const initialData = getInitialFormDataForNew()
      const { selectedContacts: _, ...prospectDataWithoutContacts } = prospect
      return { ...initialData, ...prospectDataWithoutContacts }
    }

    const initialData = getInitialFormDataForNew()
    const selectedFundId = (location.state as { selectedFundId?: string } | null)?.selectedFundId
    if (selectedFundId && Array.isArray(fundOptions)) {
      const isValidFund = fundOptions.some((f) => f.id === selectedFundId)
      if (isValidFund) {
        const po = pipelineOriginationForFundId(selectedFundId, fundOptions)
        return {
          ...initialData,
          [ProspectFields.prospects__fund]: selectedFundId,
          ...(po ? { [ProspectFields.prospects__pipeline_origination]: po } : {})
        }
      }
    }
    return initialData
  })

  // A new prospect may only be created into a stage the user may move prospects into.
  const applyStageMoveRights = useStageMoveRights()

  const pipelineStageOptions = useMemo(() => {
    const prospectPipelineId =
      formData[ProspectFields.prospects__pipeline_origination] || defaultFundraisingPipelineId

    if (!prospectPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
      return []
    }

    const filteredConfigs = pipelineConfigs.filter(
      (config) => config.pipeline === prospectPipelineId && config.visible_tbl === true
    )

    const allPipelineStageOptions = filteredConfigs
      .map((config) => {
        const stage = pipelineStages.find((s) => s.id === config.stage)
        return {
          value: config.id,
          label: stage?.name || '',
          sequence: config.sequence_tbl || config.sequence_kb || 0
        }
      })
      .filter((option) => option.label && typeof option.label === 'string')
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))

    return applyStageMoveRights(allPipelineStageOptions, null)
  }, [formData, defaultFundraisingPipelineId, pipelineConfigs, pipelineStages, applyStageMoveRights])

  // Fund picker gate (fund status, 2026-09-04). scope 'fundraising': this
  // chooses a fund to RAISE INTO, so a closed raise does not belong in the
  // list. The current selection is pinned, so reopening a prospect on a fund
  // whose raise has since closed still shows it.
  //
  // NOTE `fundOptions` above is the RAW funds array despite its name, and it
  // stays ungated: pipelineOriginationForFundId resolves through it, and a
  // gated-out fund must still resolve its origination pipeline.
  //
  // `pinnedFundId` is the form's CURRENT fund selection. It has to live in
  // component scope: the `selectedFundId` locals above and in the reset effect
  // are block-scoped to their closures (they carry the fund handed over in
  // location.state), and reading one of them here threw a ReferenceError on
  // every render of the New Prospect drawer.
  const pinnedFundId = (formData[ProspectFields.prospects__fund] as string | null | undefined) || null
  const { options: normalizedFundOptions, optionsFooter: fundOptionsFooter } = useFundFieldOptions({
    scope: 'fundraising',
    pin: pinnedFundId
  })

  const normalizedCurrencyOptions = useMemo(() => {
    if (!currencyOptions || !Array.isArray(currencyOptions)) return []
    return currencyOptions
      .map((option: any) => {
        const label = option.label || option.name || option.code || ''
        const value = option.value || option.id || option.code || ''
        return {
          value,
          label
        }
      })
      .filter((option: any) => option.label && option.value)
  }, [currencyOptions])

  const normalizedIndustryOptions = useMemo(() => {
    if (!industryOptions || !Array.isArray(industryOptions)) return []
    return industryOptions
      .map((option: any) => {
        const label = option.label || option.name || ''
        const value = option.value || option.id || ''
        return {
          value,
          label
        }
      })
      .filter((option: any) => option.label && option.value)
  }, [industryOptions])

  const helpers = useMemo<ProspectFormHelpers>(
    () => ({
      getCurrencyById,
      getCurrencyOptions: () => normalizedCurrencyOptions,
      getPipelineStageOptions: () => pipelineStageOptions,
      getFundOptions: () => normalizedFundOptions,
      getFundOptionsFooter: () => fundOptionsFooter,
      getLookupOptions: (category: string) => {
        if (category === LOOKUP_CATEGORY_INDUSTRY) return normalizedIndustryOptions
        return []
      },
      getCompanyDetailsById: null
    }),
    [getCurrencyById, normalizedCurrencyOptions, pipelineStageOptions, normalizedFundOptions, fundOptionsFooter, normalizedIndustryOptions]
  )

  const { detailsByCategory } = useLookups()
  const investorLookupId =
    detailsByCategory?.['Global Configs']?.find((option) => option.label === 'internal_company_id')?.value || null

  const prevInvestorIdRef = useRef(formData[ProspectFields.prospects__investor] || null)
  const prevIsNewInvestorRef = useRef(formData[ProspectFields.prog__is_new_investor] || false)

  const [selectedContacts, setSelectedContacts] = useState<NewProspectContacts>(() => {
    const defaultContacts: NewProspectContacts = {
      introduced_by: [],
      sourced_by: [],
      lead: []
    }

    if (currentUser) {
      const currentUserContact = transformSystemUserToContact(currentUser)
      defaultContacts.sourced_by = [currentUserContact]
      defaultContacts.lead = [currentUserContact]
    }

    return defaultContacts
  })

  const [prospectTeamLoading, setProspectTeamLoading] = useState(false)

  const transformUserEntryToContact = useCallback((userEntry: any) => {
    const displayName =
      userEntry[UserEntryFields.contacts__preferred_name] || userEntry[UserEntryFields.contacts__first_name] || ''
    const fullName = userEntry[UserEntryFields.contacts__last_name]
      ? `${displayName} ${userEntry[UserEntryFields.contacts__last_name]}`.trim()
      : displayName

    return {
      label: fullName || userEntry[UserEntryFields.contacts__email] || UNKNOWN_CONTACT,
      value: userEntry[UserEntryFields.contacts__id] || '',
      email: userEntry[UserEntryFields.contacts__email] || '',
      first_name: userEntry[UserEntryFields.contacts__first_name] || '',
      last_name: userEntry[UserEntryFields.contacts__last_name] || '',
      preferred_name: userEntry[UserEntryFields.contacts__preferred_name] || '',
      full_name: userEntry[UserEntryFields.prog__display_name] || fullName,
      data: userEntry
    }
  }, [])

  // Introduced By and Sourced By follow the investor: they become the account's own sourcing contacts.
  const loadInvestorSourcedBy = useCallback(
    async (investorId: string) => {
      setProspectTeamLoading(true)
      try {
        const response = await fetchCompanySourcingContacts(investorId)
        if (response.data && !response.error) {
          const introducedByContacts = (response.data.contact_accounts__introduced_by || []).map(
            transformUserEntryToContact
          )
          const sourcedByContacts = (response.data.contact_accounts__sourced_by || []).map(
            transformUserEntryToContact
          )

          setSelectedContacts((prev) => ({
            ...prev,
            introduced_by: introducedByContacts,
            sourced_by: sourcedByContacts
          }))
        }
      } catch (error) {
        console.error('Error fetching investor sourcing contacts:', error)
      } finally {
        setProspectTeamLoading(false)
      }
    },
    [transformUserEntryToContact]
  )

  const prospectInfoFields = useMemo(() => getProspectInfoFields(formData, helpers), [formData, helpers])

  const newInvestorFields = useMemo(() => getNewInvestorFields(formData, helpers), [formData, helpers])

  // An existing investor owns its Introduced by / Sourced by links: the drawer
  // shows them read-only, does not send them, and points the user at the
  // investor form to change them.
  const sourcingAccountId = formData[ProspectFields.prog__is_new_investor]
    ? null
    : extractCompanyId(formData[ProspectFields.prospects__investor])
  const sourcingLocked = !!sourcingAccountId
  const prospectTeamFields = useMemo(
    () => getProspectTeamFields(formData, selectedContacts, investorLookupId),
    [formData, selectedContacts, investorLookupId]
  )

  // Introduced by / Sourced by follow the existing investor: loaded whenever the
  // drawer comes to hold one - picked in the form, handed in with a seed (the
  // account form's "+ Prospect"), or restored with a draft (whose copy may be
  // out of date).
  const loadedSourcingAccountIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (loadedSourcingAccountIdRef.current === sourcingAccountId) return
    loadedSourcingAccountIdRef.current = sourcingAccountId
    if (sourcingAccountId) {
      void loadInvestorSourcedBy(sourcingAccountId)
    }
  }, [sourcingAccountId, loadInvestorSourcedBy])

  // Section holding the first validation error + a counter bumped per Save
  // click; the owning InputManager re-scrolls/focuses that field (same
  // mechanism as the deals drawer — see validationErrorsVersion).
  const [errorFocus, setErrorFocus] = useState<{
    section: 'prospectInfo' | 'prospectTeam' | 'newInvestorInfo' | null
    version: number
  }>({ section: null, version: 0 })

  const [validationErrors, setValidationErrors] = useState<Record<ValidationSection, SectionErrors>>({
    prospectInfo: {},
    newInvestorInfo: {},
    prospectTeam: {}
  })

  const isInitializingRef = useRef(false)
  const lastSyncedDataRef = useRef<string | null>(null)

  const formDataRef = useRef(formData)
  const selectedContactsRef = useRef(selectedContacts)

  useEffect(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
      syncTimeoutRef.current = null
    }

    if (isNew && prospect && Object.keys(prospect).length > 0) {
      isInitializingRef.current = true
      const initialData = getInitialFormDataForNew()
      const { selectedContacts: tempSelectedContacts, ...prospectDataWithoutContacts } = prospect
      const mergedData = { ...initialData, ...prospectDataWithoutContacts }

      formDataRef.current = mergedData
      if (tempSelectedContacts) selectedContactsRef.current = tempSelectedContacts

      setFormData(mergedData)
      form.setFieldsValue(mergedData)

      if (tempSelectedContacts) {
        setSelectedContacts(tempSelectedContacts)
      }

      lastSyncedDataRef.current = JSON.stringify({
        ...mergedData,
        selectedContacts: tempSelectedContacts || { introduced_by: [], sourced_by: [], lead: [] }
      })

      setTimeout(() => {
        isInitializingRef.current = false
      }, 100)
    } else if (isNew && (!prospect || Object.keys(prospect).length === 0)) {
      let initialData = getInitialFormDataForNew()
      const selectedFundId = (location.state as { selectedFundId?: string } | null)?.selectedFundId
      if (selectedFundId && Array.isArray(fundOptions)) {
        const isValidFund = fundOptions.some((f) => f.id === selectedFundId)
        if (isValidFund) {
          const po = pipelineOriginationForFundId(selectedFundId, fundOptions)
          initialData = {
            ...initialData,
            [ProspectFields.prospects__fund]: selectedFundId,
            ...(po ? { [ProspectFields.prospects__pipeline_origination]: po } : {})
          }
        }
      }
      setFormData(initialData)
      form.setFieldsValue(initialData)
      setSelectedContacts({
        introduced_by: [],
        sourced_by: currentUser ? [transformSystemUserToContact(currentUser)] : [],
        lead: currentUser ? [transformSystemUserToContact(currentUser)] : []
      })
      lastSyncedDataRef.current = JSON.stringify({
        ...initialData,
        selectedContacts: { introduced_by: [], sourced_by: [], lead: [] }
      })
    }
  }, [prospect, isNew, form, tempRecordUuid, currentUser, location.state, fundOptions])

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])
  useEffect(() => {
    selectedContactsRef.current = selectedContacts
  }, [selectedContacts])

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (!tempRecordUuid || !isNew || isInitializingRef.current) {
      return
    }

    const currentData = {
      ...formDataRef.current,
      selectedContacts: selectedContactsRef.current
    }
    const currentDataString = JSON.stringify(currentData)

    if (currentDataString === lastSyncedDataRef.current) {
      return
    }

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (!isInitializingRef.current) {
        const latestData = {
          ...formDataRef.current,
          selectedContacts: selectedContactsRef.current
        }
        const latestDataString = JSON.stringify(latestData)

        dispatch(
          updateActiveRecord({
            uuid: tempRecordUuid,
            updates: latestData
          })
        )
        lastSyncedDataRef.current = latestDataString
      }
    }, 300)

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [formData, selectedContacts, tempRecordUuid, isNew, dispatch])

  useEffect(() => {
    setRightMenuWidth('42%')
    return () => {
      resetRightMenuWidth()
    }
  }, [setRightMenuWidth, resetRightMenuWidth])

  useEffect(() => {
    if (currentUser) {
      const currentUserContact = transformSystemUserToContact(currentUser)
      setSelectedContacts((prev) => {
        const hasSourcedBy = prev.sourced_by?.length > 0
        const hasLead = prev.lead?.length > 0

        if (!hasSourcedBy || !hasLead) {
          return {
            ...prev,
            sourced_by: hasSourcedBy ? prev.sourced_by : [currentUserContact],
            lead: hasLead ? prev.lead : [currentUserContact]
          }
        }
        return prev
      })
    }
  }, [currentUser])

  const handleSubmit = useCallback(async () => {
    try {
      await form.validateFields()

      const formValues = form.getFieldsValue()

      const prospectInfoFields = getProspectInfoFields(formData, helpers)
      const prospectInfoValidation = validateRequiredFields(prospectInfoFields, formValues, formData)

      const newInvestorInfoFields = formData[ProspectFields.prog__is_new_investor]
        ? getNewInvestorFields(formData, helpers)
        : []
      const newInvestorInfoValidation = validateRequiredFields(newInvestorInfoFields, formValues, formData)

      const prospectTeamFields = getProspectTeamFields(formData, selectedContacts, investorLookupId)
      const prospectTeamValidation = validateRequiredFields(prospectTeamFields, formValues, formData, selectedContacts)

      const allValid =
        prospectInfoValidation.isValid && newInvestorInfoValidation.isValid && prospectTeamValidation.isValid

      setValidationErrors({
        prospectInfo: prospectInfoValidation.errors,
        newInvestorInfo: newInvestorInfoValidation.errors,
        prospectTeam: prospectTeamValidation.errors
      })

      if (!allValid) {
        // Toast names the missing fields (they may be scrolled under the
        // sticky section header); the version bump scrolls the first into view.
        const sectionErrors = [
          ['prospectInfo', prospectInfoValidation.errors],
          ['prospectTeam', prospectTeamValidation.errors],
          ['newInvestorInfo', newInvestorInfoValidation.errors]
        ] as const
        const firstSection = sectionErrors.find(([, errors]) => Object.keys(errors).length > 0)?.[0] ?? null
        const message = summariseFieldErrors(sectionErrors.map(([, errors]) => errors))
        showErrorToast(message || 'Please complete the required fields', {
          ...TOAST_CONFIG,
          key: REQUIRED_FIELDS_TOAST
        })
        setErrorFocus((prev) => ({ section: firstSection, version: prev.version + 1 }))
        return
      }
      // Validation passed — the required-fields toast (if any) is stale.
      dismissErrorToast(REQUIRED_FIELDS_TOAST)

      const prospectData = transformFormDataToProspectRecord(
        formData,
        formValues,
        // The investor's own links are not the drawer's to write.
        sourcingLocked ? { ...selectedContacts, introduced_by: [], sourced_by: [] } : selectedContacts,
        formData[ProspectFields.prog__is_new_investor]
      )

      if (!prospectData[ProspectRecordFields.prospects__id]) {
        prospectData[ProspectRecordFields.prospects__id] = crypto.randomUUID()
      }

      const validProspectFields = new Set<string>(Object.values(ProspectRecordFields))
      const cleanedData: Record<string, any> = {}

      Object.keys(prospectData).forEach((key) => {
        if (validProspectFields.has(key)) {
          cleanedData[key] = prospectData[key]
        }
      })

      try {
        validateProspectRecord(cleanedData)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Validation failed'
        showErrorToast(`Validation failed: ${errorMessage}`, TOAST_CONFIG)
        console.error('Prospect record validation failed:', error)
        return
      }

      if (onSubmit) {
        await onSubmit(cleanedData)
      }
    } catch (error) {
      // A Save that cannot complete must always say so — never console-only.
      const antdErrorFields = (error as { errorFields?: Array<{ errors?: string[] }> })?.errorFields
      const message = Array.isArray(antdErrorFields)
        ? antdErrorFields.map((f) => (f.errors || []).join(', ')).join('; ')
        : error instanceof Error
          ? error.message
          : ''
      showErrorToast(message || 'The prospect could not be saved — please try again', {
        ...TOAST_CONFIG,
        key: SUBMIT_FAILED_TOAST
      })
      console.error('Prospect form submit failed:', error)
    }
  }, [form, formData, selectedContacts, onSubmit, helpers, investorLookupId, sourcingLocked])

  // Closing the drawer retires its error toasts.
  useEffect(
    () => () => {
      dismissErrorToast(REQUIRED_FIELDS_TOAST)
      dismissErrorToast(SUBMIT_FAILED_TOAST)
    },
    []
  )

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit
    }),
    [handleSubmit]
  )

  const handleFieldChange = useCallback(
    async (field: string, value: any) => {
      let updatedFormData = null
      let isNewInvestorValue = null
      let investorIdValue = null

      setFormData((currentFormData) => {
        const result = applyBusinessRules(field, value, currentFormData, form)

        updatedFormData = result.formData
        isNewInvestorValue = updatedFormData[ProspectFields.prog__is_new_investor]
        investorIdValue = extractCompanyId(updatedFormData[ProspectFields.prospects__investor])

        if (result.formData[AccountRecordFields.accounts__business_country_code] && countriesData.length > 0) {
          const countryCode = result.formData[AccountRecordFields.accounts__business_country_code]
          const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryLabel) {
            const countryLabelField = `${AccountRecordFields.accounts__business_country_code}${FIELD_KEY_LABEL}`
            updatedFormData[countryLabelField] = countryLabel
            if (result.formUpdates) {
              result.formUpdates[countryLabelField] = countryLabel
            } else {
              result.formUpdates = { [countryLabelField]: countryLabel }
            }
          }
        }

        if (field === ProspectFields.prospects__fund) {
          const fundId = updatedFormData[ProspectFields.prospects__fund]
          const originationForForm = pipelineOriginationForFundId(
            typeof fundId === 'string' ? fundId : null,
            fundOptions
          )
          const effectivePipeline = originationForForm || defaultFundraisingPipelineId

          let nextData: Record<string, unknown> = {
            ...updatedFormData,
            [ProspectFields.prospects__pipeline_origination]: originationForForm
          }

          const stageId = nextData[ProspectFields.pipeline_prospects__stage]
          let nextFormUpdates: Record<string, unknown> = {
            ...(result.formUpdates || {}),
            [ProspectFields.prospects__pipeline_origination]: originationForForm
          }

          if (effectivePipeline) {
            const stageIdStr =
              typeof stageId === 'string' ? stageId : stageId != null ? String(stageId) : ''
            const stageOk =
              stageIdStr &&
              isPipelineStageValidForPipeline(stageIdStr, effectivePipeline, pipelineConfigs)
            if (!stageOk) {
              let resolved = getDefaultPipelineConfigForPipeline(
                effectivePipeline,
                pipelineConfigs,
                pipelineStages
              )
              if (!resolved && defaultFundraisingPipelineId) {
                resolved = getDefaultPipelineConfigForPipeline(
                  defaultFundraisingPipelineId,
                  pipelineConfigs,
                  pipelineStages
                )
              }
              if (resolved) {
                nextData = {
                  ...nextData,
                  [ProspectFields.pipeline_prospects__stage]: resolved.configId,
                  [ProspectFields.pipeline_prospects__stage__label]: resolved.label
                }
                nextFormUpdates = {
                  ...nextFormUpdates,
                  [ProspectFields.pipeline_prospects__stage]: resolved.configId,
                  [ProspectFields.pipeline_prospects__stage__label]: resolved.label
                }
              } else {
                nextData = {
                  ...nextData,
                  [ProspectFields.pipeline_prospects__stage]: '',
                  [ProspectFields.pipeline_prospects__stage__label]: ''
                }
                nextFormUpdates = {
                  ...nextFormUpdates,
                  [ProspectFields.pipeline_prospects__stage]: '',
                  [ProspectFields.pipeline_prospects__stage__label]: ''
                }
              }
            }
          }

          updatedFormData = nextData as typeof updatedFormData
          result.formUpdates = nextFormUpdates as typeof result.formUpdates
        }

        const valueChanged = updatedFormData[field] !== currentFormData[field]

        if (!valueChanged && !result.formUpdates) {
          return currentFormData
        }

        if (result.formUpdates) {
          form.setFieldsValue(result.formUpdates)
        }

        setValidationErrors((prev) => {
          const updated = { ...prev }
          VALIDATION_SECTIONS.forEach((section) => {
            if (updated[section][field]) {
              const { [field]: removed, ...rest } = updated[section]
              updated[section] = rest
            }
          })
          return updated
        })

        return updatedFormData
      })

      if (updatedFormData) {
        formDataRef.current = updatedFormData
      }

      const prevIsNewInvestor = prevIsNewInvestorRef.current

      if (field === ProspectFields.prog__is_new_investor && value === true && prevIsNewInvestor !== true) {
        setSelectedContacts((prev) => {
          const updated: NewProspectContacts = {
            introduced_by: [],
            sourced_by: [],
            lead: []
          }

          if (currentUser) {
            const currentUserContact = transformSystemUserToContact(currentUser)
            updated.sourced_by = [currentUserContact]
            updated.lead = [currentUserContact]
          }

          return updated
        })

        prevIsNewInvestorRef.current = true
        prevInvestorIdRef.current = investorIdValue || null
        return
      }

      if (field === ProspectFields.prog__is_new_investor && value === false && prevIsNewInvestor === true) {
        setSelectedContacts((prev) => {
          const updated: NewProspectContacts = {
            introduced_by: [],
            sourced_by: [],
            lead: []
          }

          if (currentUser) {
            const currentUserContact = transformSystemUserToContact(currentUser)
            updated.lead = [currentUserContact]
          }

          return updated
        })

        prevIsNewInvestorRef.current = false

        if (!investorIdValue) {
          if (currentUser) {
            const currentUserContact = transformSystemUserToContact(currentUser)
            setSelectedContacts((prev) => ({
              ...prev,
              sourced_by: [currentUserContact],
              lead: [currentUserContact]
            }))
          }
        }

        prevInvestorIdRef.current = investorIdValue || null
        return
      }

      if (field === ProspectFields.prospects__investor && !isNewInvestorValue) {
        if (!value) {
          setSelectedContacts((prev) => ({
            ...prev,
            introduced_by: [],
            sourced_by: currentUser ? [transformSystemUserToContact(currentUser)] : []
          }))
        }

        prevInvestorIdRef.current = extractCompanyId(value) || null
        return
      }

      if (field === ProspectFields.prog__is_new_investor) {
        prevIsNewInvestorRef.current = isNewInvestorValue
      }
      if (field === ProspectFields.prospects__investor) {
        prevInvestorIdRef.current = extractCompanyId(value) || null
      }
    },
    [
      form,
      countriesData,
      currentUser,
      fundOptions,
      defaultFundraisingPipelineId,
      pipelineConfigs,
      pipelineStages
    ]
  )

  return (
    <>
      <div data-debug='drawer-new-container' data-component='DrawerNew' className='space-y-1'>
        <Form
          data-debug='drawer-new-form'
          form={form}
          layout='vertical'
          className='form-compact'
          initialValues={formData}
        >
          <Row data-debug='drawer-new-main-row' gutter={12}>
            <Col span={12}>
              <FormSection title={SECTION_PROSPECT_OVERVIEW} className='mb-2'>
                <div data-debug='drawer-new-prospect-info-section'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='above'
                    fields={prospectInfoFields}
                    onChange={(key, value) => handleFieldChange(key, value)}
                    validationErrors={validationErrors.prospectInfo}
                    validationErrorsVersion={errorFocus.section === 'prospectInfo' ? errorFocus.version : undefined}
                    className='prospect-info-input-manager'
                  />
                </div>
              </FormSection>

              <FormSection title={SECTION_PROSPECT_TEAM} className='mb-2'>
                <Spin spinning={prospectTeamLoading}>
                  <div data-debug='drawer-new-prospect-team-section'>
                    {sourcingLocked && (
                      <AccountSourcingFields
                        teamFields={prospectTeamFields}
                        account={sourcingAccountId}
                        accountNoun='investor'
                        inputMode='edit'
                        labelPosition='above'
                        className='prospect-team-sourcing-input-manager'
                      />
                    )}
                    <InputManager
                      inputMode='edit'
                      labelPosition='above'
                      fields={
                        sourcingLocked
                          ? prospectTeamFields.filter((field) => !isSourcingField(field))
                          : prospectTeamFields
                      }
                      onChange={(key, value) => {
                        if (key === ProspectFields.contact_accounts__introduced_by) {
                          setSelectedContacts((prev) => ({
                            ...prev,
                            introduced_by: listFieldValue(value)
                          }))
                        } else if (key === ProspectFields.contact_accounts__sourced_by) {
                          setSelectedContacts((prev) => ({ ...prev, sourced_by: listFieldValue(value) }))
                        } else if (key === ProspectFields.contact_prospects__lead) {
                          setSelectedContacts((prev) => ({ ...prev, lead: listFieldValue(value) }))
                        }
                        setValidationErrors((prev) => {
                          if (prev.prospectTeam[key]) {
                            const { [key]: removed, ...rest } = prev.prospectTeam
                            return { ...prev, prospectTeam: rest }
                          }
                          return prev
                        })
                      }}
                      validationErrors={validationErrors.prospectTeam}
                      validationErrorsVersion={errorFocus.section === 'prospectTeam' ? errorFocus.version : undefined}
                      className='prospect-team-input-manager'
                    />
                  </div>
                </Spin>
              </FormSection>
            </Col>

            <Col span={12}>
              {formData[ProspectFields.prog__is_new_investor] && (
                <FormSection title='New Investor Information' className='mb-2' titleColor='green'>
                  <div data-debug='drawer-new-investor-info-section'>
                    <InputManager
                      inputMode='edit'
                      labelPosition='above'
                      fields={newInvestorFields}
                      onChange={(key, value) => handleFieldChange(key, value)}
                      validationErrors={validationErrors.newInvestorInfo}
                      validationErrorsVersion={errorFocus.section === 'newInvestorInfo' ? errorFocus.version : undefined}
                      className='investor-info-input-manager'
                    />
                  </div>
                </FormSection>
              )}
            </Col>
          </Row>
        </Form>
      </div>
    </>
  )
})

Drawer.displayName = 'ProspectDrawerNew'
